import { Box, Divider, Stack, Typography, IconButton } from "@mui/material";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { HubConnection } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { SystemSettingSaveMessage, NameValueModel } from "Infrastructure/Models/Messages/Messages";
import DeviceFormComponent from "../DeviceFormComponent";
import SystemSettingsActionSave from "./SystemSettingsActionSave";
import { ActionHistorySelect } from "../ActionHistorySelect";
import { ActionHistoryDto } from "Infrastructure/Api/Api";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import ClearIcon from "@mui/icons-material/Clear";
import { FormValue } from "Infrastructure/Utils/FormUtils";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";

type SystemSettingSaveFormData = {
	isValidationShown: boolean;
	name: FormValue<string>;
	value: FormValue<string>;
};

const getNewFormData = () => {
	return {
		isValidationShown: false,
		name: {
			value: "",
		},
		value: {
			value: "",
		},
	};
};

type Props = {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onSave: (message: SystemSettingSaveMessage) => void;
};

const SystemSettingsActions: FunctionComponent<Props> = (props) => {
	const { deviceStatuses, deviceStatus, hubConnection, closeDialog, onSave } =
		props;
	const showSnackbar = useSnackbar();

	if (!deviceStatuses || deviceStatuses?.length === 0) {
		return (
			<Typography variant="caption">Žádné instance k dispozici</Typography>
		);
	}

	const [form, setForm] = useState<DeviceForm>({
		devices: !!deviceStatus
			? [
					{
						name: deviceStatus.deviceName,
						isChecked: true,
						isDisabled: false,
						deviceStatus,
					},
			  ]
			: deviceStatuses.map((x) => ({
					name: x.deviceName,
					isChecked: false,
					isDisabled:
						x.deviceState !== DeviceState.Connected || x.isSelfInsurance,
					deviceStatus: x,
			  })),
	});

	// Wrap setForm to clear isHistoryLoaded on any change
	const setFormWithHistoryCheck = (
		updater: React.SetStateAction<DeviceForm>
	) => {
		setForm((prev: DeviceForm) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			if (isHistoryLoaded && !justLoadedHistory.current) {
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
			return next;
		});
	};

	const [formData, setFormData] = useState<SystemSettingSaveFormData>(getNewFormData());
	const [settingsArray, setSettingsArray] = useState<NameValueModel[]>([]);

	const [selectedHistory, setSelectedHistory] =
		useState<ActionHistoryDto | null>(null);
	const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
	const justLoadedHistory = useRef(false);

	// Add mapping functions
	function mapSystemSettingSaveToFormData(message: any): SystemSettingSaveFormData {
		return {
			isValidationShown: false,
			name: { value: "", isValid: true },
			value: { value: "", isValid: true },
		};
	}

	// Utility: Map filteredDeviceNames to deviceForm
	function updateDeviceFormWithFilteredNames(
		deviceForm: DeviceForm,
		filteredDeviceNames: string[] = []
	) {
		const unavailableDevices: string[] = [];
		
		const updatedDevices = deviceForm.devices.map((device) => {
			const shouldBeChecked = filteredDeviceNames.includes(device.name);
			
			// If device should be checked but is disabled, add to unavailable list
			if (shouldBeChecked && device.isDisabled) {
				unavailableDevices.push(device.name);
			}
			
			return {
				...device,
				isChecked: shouldBeChecked && !device.isDisabled, // Only check if not disabled
			};
		});
		
		// Show snackbar if there are unavailable devices
		if (unavailableDevices.length > 0) {
			showSnackbar(
				`Nepodařilo se načíst některé instance: ${unavailableDevices.join(', ')}`,
				"error"
			);
		}
		
		return {
			...deviceForm,
			devices: updatedDevices,
		};
	}

	// Effect: clear history selection on form change
	// biome-ignore lint/correctness/useExhaustiveDependencies: form and settingsArray are intentional change triggers
	useEffect(() => {
		if (isHistoryLoaded) {
			if (justLoadedHistory.current) {
				justLoadedHistory.current = false;
			} else {
				// Clear history when any form data changes manually
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
		}
	}, [form, settingsArray, isHistoryLoaded]);

	// Effect: clear form data when history is cleared
	useEffect(() => {
		if (!isHistoryLoaded && selectedHistory === null) {
			// Clear form data when history is cleared
			setFormData(getNewFormData());
		}
	}, [isHistoryLoaded, selectedHistory]);

	const clearAllData = () => {
		setForm((prev) => ({
			...prev,
			devices: prev.devices.map((d) => ({ ...d, isChecked: false })),
		}));
		setSettingsArray([]);
		setFormData(getNewFormData());
		setSelectedHistory(null);
		setIsHistoryLoaded(false);
	};

	return (
		<Stack>
			<Box>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					width="900px"
					justifySelf="end"
				>
					<ActionHistorySelect
						device={deviceStatus?.deviceName}
						actionTypes={[ActionType.SystemSettingSave]}
						value={selectedHistory}
						onSelect={(action) => {
							clearAllData();
							setSelectedHistory(action);
							if (action) {
								try {
									const parsed = JSON.parse(action.messageJson);
									const filteredDeviceNames = parsed.FilteredDeviceNames || parsed.filteredDeviceNames || [];
									setFormWithHistoryCheck((prev) => updateDeviceFormWithFilteredNames(prev, filteredDeviceNames));
									if (action.actionType === ActionType.SystemSettingSave) {
										const settings = parsed.Settings ?? parsed.settings ?? [];
										// Ensure settings have proper structure
										const normalizedSettings = settings.map((setting: any) => ({
											name: setting.Name ?? setting.name ?? null,
											value: setting.Value ?? setting.value ?? null,
										}));
										setSettingsArray(normalizedSettings);
										setFormData(mapSystemSettingSaveToFormData(parsed));
									}
									setIsHistoryLoaded(true);
									justLoadedHistory.current = true;
								} catch {
									// showSnackbar("Nepodařilo se načíst historickou akci.", "error");
								}
							}
						}}
						disabled={isHistoryLoaded}
					/>
					<IconButton
						size="small"
						onClick={clearAllData}
						disabled={!isHistoryLoaded}
					>
						<ClearIcon sx={{ fontSize: "1.2rem" }} />
					</IconButton>
				</Stack>
			</Box>
			{!deviceStatus && (
				<Box mb={1}>
					<Typography variant="h6" component="div">
						Instance
					</Typography>
					<Divider />
					<Box ml={1.5} mt={1}>
						<DeviceFormComponent
							deviceStatuses={deviceStatuses}
							hubConnection={hubConnection}
							deviceStatus={deviceStatus}
							form={form}
							setForm={setFormWithHistoryCheck}
						/>
					</Box>
				</Box>
			)}
			<Stack>
				<Box>
					<Typography variant="h6" component="div">
						Akce
					</Typography>
					<Divider />
				</Box>
				<SystemSettingsActionSave
					deviceForm={form}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onSave={onSave}
					formData={formData}
					setFormData={setFormData}
					settingsArray={settingsArray}
					setSettingsArray={setSettingsArray}
				/>
			</Stack>
		</Stack>
	);
};

export default SystemSettingsActions;
